import React, { useEffect, useState } from 'react';
import { 
  Card, 
  Typography, 
  Paper, 
  Box, 
  CircularProgress, 
  TextField,
  Button,
  Snackbar,
  Alert,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  useTheme,
  useMediaQuery,
  Tabs,
  Tab,
  Divider,
  Modal,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloseIcon from '@mui/icons-material/Close';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { format, parseISO } from 'date-fns';

// Custom components and utilities
import ImageViewAndUpload from './PhotoSelector/ImageViewAndUpload';
import styles from './Questionnaire/styles';
import { 
  getAnswerSheetForCurrentUser, 
  setAnswers, 
  viewAnswerSheet, 
  uploadImageUsingFile, 
  uploadImageUsingUrl, 
  getGroupAnswers 
} from '../apis/apis';

// Helper functions for date formatting
const formatDate = (dateString) => {
  if (!dateString) return '';
  return format(parseISO(dateString), 'MMM d, yyyy');
};

const Questionnaire = ({ 
  selectedPeriod, 
  currentGroup, 
  selectedMember,
  isCurrentPeriod,
  groupMembers = []
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  // State management
  const [answerSheet, setAnswerSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [userAnswers, setUserAnswers] = useState([]);
  const [groupAnswers, setGroupAnswers] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  const [existingImages, setExistingImages] = useState([]);
  const [selectedImages, setSelectedImages] = useState([]);

  // Fetch data on component mount and when dependencies change
  useEffect(() => {
    fetchQuestionnaireData();
  }, [selectedPeriod, currentGroup, selectedMember, isCurrentPeriod]);

  // Data fetching function
  const fetchQuestionnaireData = async () => {
    if (!selectedPeriod || !currentGroup) return;
    
    setLoading(true);
    setError(null);
    
    try {
      if (isCurrentPeriod) {
        await fetchCurrentPeriodData();
      } else {
        await fetchPastPeriodData();
      }
    } catch (err) {
      setError('Failed to fetch questionnaire data. Please try again.');
      console.error('Error fetching answer sheet:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch current period data for editing
  const fetchCurrentPeriodData = async () => {
    const data = await getAnswerSheetForCurrentUser(currentGroup, selectedPeriod);
    setAnswerSheet(data);
    setUserAnswers(data.answers.length > 0 
      ? data.answers 
      : new Array(data.questions.length).fill('')
    );
    setExistingImages(data.imageLinks.map(url => ({
      preview: url,
      isExisting: true,
      url: url
    })));
  };

  // Fetch past period data for viewing
  const fetchPastPeriodData = async () => {
    // For past periods, always fetch group answers
    const groupData = await getGroupAnswers(currentGroup, selectedPeriod);
    setGroupAnswers(groupData);
    
    // Also fetch individual data if someone is selected
    if (selectedMember) {
      const data = await viewAnswerSheet(selectedMember.id, currentGroup, selectedPeriod);
      setAnswerSheet(data);
    }
  };

  // Event handlers
  const handleAnswerChange = (index, value) => {
    setUserAnswers(prev => {
      const newAnswers = [...prev];
      newAnswers[index] = value;
      return newAnswers;
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  // Image handling
  const uploadImage = async (img) => {
    if (img.file) {
      const fileResponse = await uploadImageUsingFile(img.file);
      return fileResponse.url;
    } else {
      const urlResponse = await uploadImageUsingUrl(img.preview);
      return urlResponse.url;
    }
  };

  const uploadImages = async () => {
    if (selectedImages.length === 0) return [];

    try {
      const uploadPromises = selectedImages.map(uploadImage);
      return await Promise.all(uploadPromises);
    } catch (error) {
      console.error('Error uploading images:', error);
      throw error;
    }
  };

  // Form submission
  const handleSubmit = async () => {
    try {
      setLoading(true);
      
      // Upload new images
      const newImageUrls = await uploadImages();

      // Combine existing and new image URLs
      const allImageUrls = [
        ...existingImages.map(img => img.url),
        ...newImageUrls
      ];

      // Save answers to server
      await setAnswers(
        answerSheet.groupId,
        answerSheet.weekStart,
        userAnswers,
        allImageUrls
      );

      // Update UI state
      setSnackbar({
        open: true,
        message: 'Your responses and images have been saved successfully!',
        severity: 'success'
      });
      
      setExistingImages(allImageUrls.map(url => ({
        preview: url,
        isExisting: true,
        url: url
      })));
      setSelectedImages([]); // Clear new images after successful upload
      
    } catch (err) {
      setSnackbar({
        open: true,
        message: 'Failed to save your responses. Please try again.',
        severity: 'error'
      });
      console.error('Error submitting answers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get user name from ID
  const getUserNameById = (userId) => {
    if (!groupMembers || groupMembers.length === 0) return null;
    
    const member = groupMembers.find(member => member.id === userId);
    return member ? member.name : null;
  };

  // Conditional renders for different states
  if (!selectedPeriod || !currentGroup) {
    return (
      <Paper elevation={3} sx={styles.container}>
        <Typography variant="body1" color="text.primary">
          Please select a group and period to view the questionnaire.
        </Typography>
      </Paper>
    );
  }

  if (loading && !answerSheet && !groupAnswers) {
    return (
      <Card sx={styles.loadingCard}>
        <CircularProgress />
      </Card>
    );
  }

  if (error) {
    return (
      <Card sx={styles.container}>
        <Alert severity="error" variant="outlined">
          {error}
        </Alert>
      </Card>
    );
  }

  // Render components
  return (
    <Card sx={styles.container}>
      {/* Header */}
      <HeaderSection 
        isCurrentPeriod={isCurrentPeriod}
        answerSheet={answerSheet}
        groupAnswers={groupAnswers}
        isMobile={isMobile}
      />

      {/* Current period edit view */}
      {isCurrentPeriod && answerSheet && (
        <CurrentPeriodView 
          answerSheet={answerSheet}
          userAnswers={userAnswers}
          handleAnswerChange={handleAnswerChange}
          existingImages={existingImages}
          setExistingImages={setExistingImages}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          isMobile={isMobile}
        />
      )}

      {/* Past period group view with tabs to reduce scrolling */}
      {!isCurrentPeriod && groupAnswers && (
        <PastPeriodView 
          groupAnswers={groupAnswers}
          getUserNameById={getUserNameById}
          theme={theme}
          isMobile={isMobile}
        />
      )}

      {/* Submit button - only shown for current period */}
      {isCurrentPeriod && (
        <Box sx={{ mt: 3, mb: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleSubmit}
            disabled={loading}
            sx={styles.submitButton}
            fullWidth
            size={isMobile ? "large" : "medium"}
            disableElevation
          >
            {loading ? 'Saving responses and uploading images...' : 'Save Responses'}
          </Button>
        </Box>
      )}

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          elevation={6}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Card>
  );
};

// Helper component for the header section
const HeaderSection = ({ isCurrentPeriod, answerSheet, groupAnswers, isMobile }) => (
  <Box sx={styles.header}>
    <Typography variant={isMobile ? "h5" : "h4"} gutterBottom color="text.primary" sx={{ fontWeight: 600 }}>
      {isCurrentPeriod 
        ? 'What did you do this week?' 
        : 'Group Weekly Updates'
      }
    </Typography>
    <Typography variant="subtitle1" color="text.secondary">
      {isCurrentPeriod 
        ? 'Share your thoughts and experiences' 
        : 'View all group responses'} from {formatDate(answerSheet?.weekStart || groupAnswers?.weekStart)} to {formatDate(answerSheet?.weekEnd || groupAnswers?.weekEnd)}
    </Typography>
  </Box>
);

// Component for current period view
const CurrentPeriodView = ({ 
  answerSheet, 
  userAnswers, 
  handleAnswerChange, 
  existingImages, 
  setExistingImages, 
  selectedImages, 
  setSelectedImages,
  isMobile 
}) => (
  <Container disableGutters maxWidth={false} sx={{ px: { xs: 0, sm: 1 } }}>
    {answerSheet.questions.map((question, index) => (
      <Box key={index} sx={styles.questionSection}>
        <Accordion>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon color="primary" />}
            aria-controls={`question-${index}-content`}
            id={`question-${index}-header`}
          >
            <Typography sx={styles.questionTitle}>
              {index + 1}. {question}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <TextField
              fullWidth
              multiline
              rows={isMobile ? 4 : 3}
              variant="outlined"
              placeholder="Share your thoughts here..."
              value={userAnswers[index] || ''}
              onChange={(e) => handleAnswerChange(index, e.target.value)}
              sx={styles.answerField}
            />
          </AccordionDetails>
        </Accordion>
      </Box>
    ))}
    
    <ImageViewAndUpload 
      existingImages={existingImages} 
      setExistingImages={setExistingImages} 
      selectedImages={selectedImages} 
      setSelectedImages={setSelectedImages} 
      allowEdit={true}
    />
  </Container>
);

// Component for past period group view with tabs to reduce scrolling
const PastPeriodView = ({ groupAnswers, getUserNameById, theme, isMobile }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [showImages, setShowImages] = useState(false);
  const [modalImage, setModalImage] = useState(null);
  const [expandedUsers, setExpandedUsers] = useState({});
  
  const handleTabChange = (event, newValue) => {
    if (newValue === groupAnswers.questions.length) {
      setShowImages(true);
    } else {
      setActiveTab(newValue);
      setShowImages(false);
    }
  };
  
  const handleImageClick = (imageUrl) => {
    setModalImage(imageUrl);
  };
  
  const handleCloseModal = () => {
    setModalImage(null);
  };
  
  const toggleUserImages = (userId) => {
    setExpandedUsers(prev => ({
      ...prev,
      [userId]: !prev[userId]
    }));
  };
  
  // Initialize expanded state for users with images when the component mounts or when groupAnswers changes
  useEffect(() => {
    if (groupAnswers?.userAnswerSheets) {
      const initialExpandedState = {};
      groupAnswers.userAnswerSheets.forEach(user => {
        if (user.imageLinks && user.imageLinks.length > 0) {
          initialExpandedState[user.userId] = true; // Default to expanded
        }
      });
      setExpandedUsers(initialExpandedState);
    }
  }, [groupAnswers]);
  
  // Check if any users have images
  const hasImages = groupAnswers.userAnswerSheets && 
    groupAnswers.userAnswerSheets.some(user => 
      user.imageLinks && user.imageLinks.length > 0
    );
  
  return (
    <Container disableGutters maxWidth={false} sx={{ px: { xs: 0, sm: 1 } }}>
      {/* Tab navigation for questions */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={showImages ? groupAnswers.questions.length : activeTab} 
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="questionnaire tabs"
          sx={{
            '& .MuiTab-root': {
              minWidth: 'auto',
              px: 2,
              py: 1,
              fontSize: { xs: '0.85rem', sm: '0.9rem' },
              fontWeight: 500,
            }
          }}
        >
          {groupAnswers.questions && groupAnswers.questions.map((question, qIndex) => (
            <Tab 
              key={qIndex} 
              label={`Q${qIndex + 1}`} 
              id={`question-tab-${qIndex}`}
              aria-controls={`question-tabpanel-${qIndex}`}
            />
          ))}
          {hasImages && (
            <Tab 
              label="Photos" 
              id="photos-tab"
              aria-controls="photos-tabpanel"
            />
          )}
        </Tabs>
      </Box>
      
      {/* Active question panel */}
      {!showImages && groupAnswers.questions && (
        <Box sx={{ mb: 4 }}>
          <Typography variant="h6" sx={styles.questionTitle} gutterBottom>
            {activeTab + 1}. {groupAnswers.questions[activeTab]}
          </Typography>
          <Divider sx={{ my: 2 }} />
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 3 }}>
            {groupAnswers.userAnswerSheets && groupAnswers.userAnswerSheets.map((userAnswer, uIndex) => (
              <Box key={`user-${uIndex}`} sx={styles.userAnswerBox}>
                <Typography variant="subtitle2" sx={styles.userName}>
                  {getUserNameById(userAnswer.userId) || userAnswer.userName || `User ${uIndex + 1}`}
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  variant="outlined"
                  value={userAnswer.answers[activeTab] || ''}
                  sx={styles.answerField}
                  disabled={true}
                  InputProps={{
                    sx: {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.03)' 
                        : 'rgba(0,0,0,0.01)',
                      color: theme.palette.text.primary,
                      borderRadius: 1.5,
                    }
                  }}
                  placeholder="No answer provided"
                />
              </Box>
            ))}
          </Box>
        </Box>
      )}
      
      {/* Photos tab panel */}
      {showImages && groupAnswers.userAnswerSheets && (
        <Box sx={styles.imageGallerySection}>
          <Typography variant="h6" sx={styles.imageGalleryTitle}>
            Group Photos
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Button 
              variant="outlined" 
              size="small"
              onClick={() => {
                const allExpanded = Object.values(expandedUsers).every(v => v);
                const newState = {};
                groupAnswers.userAnswerSheets.forEach(user => {
                  if (user.imageLinks && user.imageLinks.length > 0) {
                    newState[user.userId] = !allExpanded;
                  }
                });
                setExpandedUsers(newState);
              }}
            >
              {Object.values(expandedUsers).every(v => v) ? 'Collapse All' : 'Expand All'}
            </Button>
          </Box>
          {groupAnswers.userAnswerSheets.map((userAnswer, uIndex) => (
            userAnswer.imageLinks && userAnswer.imageLinks.length > 0 && (
              <Box key={`user-images-${uIndex}`} sx={styles.userImagesSection}>
                <Box 
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    cursor: 'pointer',
                    py: 1,
                  }}
                  onClick={() => toggleUserImages(userAnswer.userId)}
                >
                  <Typography variant="subtitle2" sx={styles.userImageName}>
                    {getUserNameById(userAnswer.userId) || userAnswer.userName || `User ${uIndex + 1}`}
                    <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                      ({userAnswer.imageLinks.length} {userAnswer.imageLinks.length === 1 ? 'photo' : 'photos'})
                    </Typography>
                  </Typography>
                  <IconButton 
                    size="small" 
                    edge="end"
                    aria-label={expandedUsers[userAnswer.userId] ? 'collapse' : 'expand'}
                  >
                    {expandedUsers[userAnswer.userId] ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                  </IconButton>
                </Box>
                {expandedUsers[userAnswer.userId] && (
                  <Box sx={{ ...styles.imageGrid, mt: 2 }}>
                    {userAnswer.imageLinks.map((imageUrl, imgIndex) => (
                      <Box 
                        key={`img-${imgIndex}`} 
                        component="img"
                        src={imageUrl}
                        alt={`${userAnswer.userName}'s image ${imgIndex + 1}`}
                        sx={{
                          ...styles.imageItem,
                          cursor: 'pointer',
                        }}
                        onClick={() => handleImageClick(imageUrl)}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )
          ))}
        </Box>
      )}

      {/* Navigation buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4, mb: 2 }}>
        <Button 
          variant="outlined"
          disabled={showImages ? false : activeTab === 0}
          onClick={() => {
            if (showImages) {
              setActiveTab(groupAnswers.questions.length - 1);
              setShowImages(false);
            } else {
              setActiveTab(prev => prev - 1);
            }
          }}
          sx={{ minWidth: '100px' }}
        >
          Previous
        </Button>
        
        <Button
          variant="outlined"
          disabled={showImages}
          onClick={() => {
            if (activeTab === groupAnswers.questions.length - 1 && hasImages) {
              setShowImages(true);
            } else {
              setActiveTab(prev => prev + 1);
            }
          }}
          sx={{ minWidth: '100px' }}
        >
          Next
        </Button>
      </Box>
      
      {/* Image Modal */}
      <Modal
        open={!!modalImage}
        onClose={handleCloseModal}
        aria-labelledby="image-modal"
        aria-describedby="expanded image view"
        sx={styles.modalBackdrop}
      >
        <Box sx={styles.modalContent}>
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 10,
                right: 10,
                backgroundColor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.7)',
                }
              }}
            >
              <CloseIcon />
            </IconButton>
            <Box
              component="img"
              src={modalImage}
              alt="Expanded view"
              sx={styles.modalImage}
            />
          </Box>
        </Box>
      </Modal>
    </Container>
  );
};

export default Questionnaire;
